import * as React from 'react';
import * as navigate from 'app/area/shared/navigation';
import { STATE } from 'app/constants';
import { containerFactory, DefaultContainerProps } from 'app/infrastructure/redux';
import { ListWrapper } from './components/list-wrapper';
import { ListRenderer } from './components/list-renderer';
import { actionCreators } from './actions';
import { favouriteAdminTranslation } from '../constants';
import { FavouriteAdminState } from '../types-store';
import { scrollToTop } from 'app/infrastructure/platform/routing';
import { ChangeOwnerModal } from 'app/area/favourite-administration/list/change-owner/change-owner';
import { OptionsProps, withOptions } from 'app/components/options-handler';
import { ChangeUserGroupsModal } from 'app/area/favourite-administration/list/change-user-groups/change-user-goups';
import { ChangeUsersModal } from 'app/area/favourite-administration/list/change-users/change-users';
import { getBackRoute } from 'app/area/favourite-administration/form/route';
import { getFavouriteAdminAccess } from 'app/area/favourite-administration/get-access';
import { ChangeOptionType } from 'app/area/favourite-administration/list/types';

type FavouriteAdminListProps = DefaultContainerProps
  & OptionsProps
  & FavouriteAdminState
  & typeof actionCreators;

interface FavouriteAdminListState {
  changeOwner: boolean;
  changeUserGroups: boolean;
  changeUsers: boolean;
}

class FavouriteAdminListComponent extends React.PureComponent<FavouriteAdminListProps, FavouriteAdminListState> {
  public static statePath: string = STATE.favouriteAdministration;

  public state: FavouriteAdminListState = {
    changeOwner: false,
    changeUserGroups: false,
    changeUsers: false
  };

  public componentWillUnmount() {
    this.props.clearList();
  }

  public componentDidMount() {
    this.clearUrlParams();
    scrollToTop();
  }

  private toggleChangeOwner = () => {
    this.setState({ changeOwner: !this.state.changeOwner });
    this.props.clearChangeOwner();
  };

  private toggleChangeUserGroups = () => {
    this.setState({ changeUserGroups: !this.state.changeUserGroups });
  };

  private toggleChangeUsers = () => {
    this.setState({ changeUsers: !this.state.changeUsers });
  };

  private changeOwner = (mode: ChangeOptionType, ownersToAdd: number[]) => {
    const { list: { changeOwner: { form: { fromOwners: { value: ownerValue } = {}, toOwners, applyOnDescendants = false } = {} } = {},
      numberOfResults, requestedSearchModel }, translation, loadList } = this.props;

    const toOwnerValues = mode === ChangeOptionType.Add || mode === ChangeOptionType.Remove
      ? ownersToAdd
      : toOwners?.map(owner => owner.value);

    this.props.changeOwner(
      mode,
      ownerValue,
      toOwnerValues,
      applyOnDescendants,
      numberOfResults,
      requestedSearchModel,
      translation.favourites_updated,
      () => {
        this.toggleChangeOwner();
        loadList(requestedSearchModel);
      });
  };

  private changeUserGroups = () => {
    const { list: { changeUserGroups: {
      state,
      form: {
        replaceExistingUserGroups = [],
        applyOnDescendants = false
      } = {}
    } = {}, numberOfResults, requestedSearchModel },
      translation, loadList } = this.props;

    this.props.changeUserGroups(
      state,
      replaceExistingUserGroups,
      applyOnDescendants,
      numberOfResults,
      requestedSearchModel,
      translation.favourites_updated,
      () => {
        this.toggleChangeUserGroups();
        loadList(requestedSearchModel);
      });
  };

  private changeUsers = () => {
    const { list: { changeUsers: {
      state,
      form: {
        replaceExistingUsers = [],
        applyOnDescendants = false
      } = {}
    } = {}, numberOfResults, requestedSearchModel },
      translation, loadList } = this.props;

    this.props.changeUsers(
      state,
      replaceExistingUsers,
      applyOnDescendants,
      numberOfResults,
      requestedSearchModel,
      translation.favourites_updated,
      () => {
        this.toggleChangeUsers();
        loadList(requestedSearchModel);
      });
  };

  private clearUrlParams = () => navigate.to({ pathname: getBackRoute() }, { state: { internal: true } });

  public render() {

    const { list, loadList, deleteFavourite, displayError, displayRegenerateAggregatedTables, 
      locale, sharedState, translation } = this.props;

    return <ListWrapper
      displayError={displayError}
      sharedState={sharedState}
      translation={translation}>
      <ListRenderer
        listState={list}
        loadList={loadList}
        deleteFavourite={deleteFavourite}
        changeOwner={this.toggleChangeOwner}
        changeUserGroups={this.toggleChangeUserGroups}
        changeUsers={this.toggleChangeUsers}
        displayRegenerateAggregatedTables={getFavouriteAdminAccess(sharedState) && displayRegenerateAggregatedTables}
        translation={translation}
      />

      {this.state.changeOwner && <ChangeOwnerModal
        onClose={this.toggleChangeOwner}
        save={this.changeOwner}
        validation={this.props.list.validation}
        list={list}

        loadOptions={this.props.loadOptions}
        options={this.props.options}
        updateByKey={this.props.updateChangeOwnerFieldsByKey}

        resetValidation={this.props.resetChangeOwnerValidation}
        resetOwnersValidation={this.props.resetOwnersValidation}
        translation={translation}
        updateIsDirty={this.props.updateChangeOwnerIsDirty}
        locale={locale}
      />}

      {this.state.changeUserGroups && <ChangeUserGroupsModal
        onClose={this.toggleChangeUserGroups}
        save={this.changeUserGroups}
        validation={this.props.list.changeUserGroups.validation}
        list={list}
        locale={locale}

        loadOptions={this.props.loadOptions}
        options={this.props.options}
        updateByKey={this.props.updateChangeUserGroupsFieldsByKey}

        resetValidation={this.props.resetChangeUserGroupsValidation}
        translation={translation}
        updateIsDirty={this.props.updateChangeUserGroupsIsDirty}
        updateState={this.props.updateChangeUserGroupsState}
        clearChangeUserGroups={this.props.clearChangeUserGroups}
      />}

      {this.state.changeUsers && <ChangeUsersModal
        onClose={this.toggleChangeUsers}
        save={this.changeUsers}
        validation={this.props.list.changeUsers.validation}
        list={list}
        locale={locale}

        loadOptions={this.props.loadOptions}
        options={this.props.options}
        updateByKey={this.props.updateChangeUsersFieldsByKey}

        resetValidation={this.props.resetChangeUsersValidation}
        translation={translation}
        updateIsDirty={this.props.updateChangeUsersIsDirty}
        updateState={this.props.updateChangeUsersState}
        clearChangeUsers={this.props.clearChangeUsers}
      />}
    </ListWrapper>;
  }
}

const container = containerFactory(withOptions(FavouriteAdminListComponent), actionCreators, undefined, favouriteAdminTranslation);

export { container as FavouriteAdminList };
